import type { LocationGroup } from "@/locationTable/types";
import type { PoiDataGrid } from "@/threads/DataProcessingThread/types";
import type { ExtendedCoordinates } from "@/types/location";
import type { Coordinate } from "@mm/api.meteomatics.com";
import type { Feature, Polygon } from "geojson";
export type LocationTablePoint = ExtendedCoordinates & {
  label?: string | null;
  groupName?: string;
  isPolygon?: boolean;
};

const toStrOrUndefined = (v: unknown): string | undefined => (v != null ? String(v) : undefined);

export const getPointsFromTableGroups = (tableGroups?: LocationGroup[]): Coordinate[] => {
  if (!tableGroups) {
    return [];
  }
  const points: Coordinate[] = [];
  for (const tableGroup of tableGroups) {
    for (const location of tableGroup.locations) {
      points.push({ lat: location.lat, lon: location.lon });
    }
  }
  return points;
};

export const getPolygonFeatureFromPolygonString = (polygonStr: string): Feature<Polygon> => {
  // Split string into numbers and build [lon, lat] pairs
  const values = polygonStr?.split(",").map(Number) || [];
  const polygonCoordinates: number[][] = [];

  for (let i = 0; i < values.length; i += 2) {
    const lon = values[i];
    const lat = values[i + 1];
    if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
      polygonCoordinates.push([lon, lat]);
    }
  }
  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [polygonCoordinates],
    },
  };
};

export const getPolygonsFromTableGroups = (tableGroups?: LocationGroup[]): Feature<Polygon>[] => {
  if (!tableGroups) {
    return [];
  }

  const polygons: Feature<Polygon>[] = [];

  for (const tableGroup of tableGroups) {
    for (const location of tableGroup.locations) {
      if (location.polygon) {
        const polygon: Feature<Polygon> = getPolygonFeatureFromPolygonString(location.polygon);

        if (polygon != null) {
          polygons.push(polygon);
        }
      }
    }
  }

  return polygons;
};

export const getPointsWithInfoFromTableGroups = (tableGroups: LocationGroup[] | null): LocationTablePoint[] => {
  if (!tableGroups) {
    return [];
  }
  const points: LocationTablePoint[] = [];
  for (const tableGroup of tableGroups) {
    for (const location of tableGroup.locations) {
      const { lat, lon, label, ...rest } = location;
      points.push({
        lat: lat,
        lon: lon,
        label: toStrOrUndefined(label),
        groupName: tableGroup.name,
        ...rest,
      });
    }
  }
  return points;
};

export const getGroupsFromDataGrid = (dataGrid?: PoiDataGrid): LocationGroup[] => {
  if (!dataGrid) {
    return [];
  }

  const groupMap: { [key: string]: LocationGroup } = {};

  for (const poiData of dataGrid) {
    const groupName = poiData.groupName || null;
    if (!groupMap[groupName]) {
      groupMap[groupName] = {
        name: toStrOrUndefined(groupName),
        locations: [],
      };
    }

    const { lat, lon, label, name, groupName: _groupName, ...rest } = poiData;
    groupMap[groupName].locations.push({
      ...rest,
      lat,
      lon,
      label: toStrOrUndefined(label) ?? toStrOrUndefined(name),
    });
  }

  return Object.values(groupMap);
};
